import React from 'react';
import { PlayCircle, X } from 'lucide-react';

// Classi statiche per accento (Tailwind non risolve classi costruite dinamicamente)
const ACCENT_CLASSES = {
  purple: {
    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
    icon: 'text-purple-600 dark:text-purple-400'
  },
  blue: {
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    icon: 'text-blue-600 dark:text-blue-400'
  }
} as const;

interface VideoModalProps {
  title: string;
  subtitle?: string;
  /** URL completo di embed (es. youtube.com/embed/... o youtube-nocookie.com/embed/...) */
  embedSrc: string;
  onClose: () => void;
  accent?: keyof typeof ACCENT_CLASSES;
  footer?: React.ReactNode;
}

const VideoModal: React.FC<VideoModalProps> = ({
  title,
  subtitle,
  embedSrc,
  onClose,
  accent = 'purple',
  footer
}) => {
  const accentCls = ACCENT_CLASSES[accent];

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${accentCls.iconBg} rounded-lg flex items-center justify-center`}>
              <PlayCircle className={`w-6 h-6 ${accentCls.icon}`} />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white">
                {title}
              </h3>
              {subtitle && (
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Video 16:9 */}
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            className="absolute top-0 left-0 w-full h-full border-0"
            src={embedSrc}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </div>

        {/* Footer */}
        {footer && (
          <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoModal;
